import { useEffect, useRef, useState } from 'react'
import {
  CrudKeyRefusedError,
  withKeyReissue,
  createCluster,
  updateCluster,
  deleteCluster
} from '@aytm/app/stats/page/question-views/hm/components/controls/lib/api'
import { deleteSurveyAsset, updateSurveyAsset } from '@aytm/app/stats/lib/stat-page'
import { calcClusterModel } from '@aytm/app/stats/page/question-views/hm/components/controls/cluster-form/lib/utils'
import {
  CLUSTER_FORM_MODE,
  DEFAULT_ERROR,
  HM_CLUSTER
} from '@aytm/app/stats/page/question-views/hm/components/controls/lib/constants'

// external

export const useClusterForm = ({
  clusters,
  currentCluster,
  visualPosition,
  clusterFormMode,
  tip,
  onChangeModel,
  changeClustersForm,
  onUpdateData,
  getLassoData,
  onFetchCRUDKey
}) => {
  const [title, setTitle] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  const abortControllerRef = useRef(null)

  const isSaveDisabled = tip === 1

  const showError = text => setError(text || DEFAULT_ERROR)
  const hideError = () => setError(null)

  const handleCancel = (isCloseForm = false) => {
    abortControllerRef.current?.abort()
    abortControllerRef.current = null

    setIsLoading(false)
    setError(null)
    setTitle('')

    if (isCloseForm) {
      changeClustersForm(CLUSTER_FORM_MODE.OFF)
    }
  }

  useEffect(() => {
    setTitle(clusterFormMode === CLUSTER_FORM_MODE.EDIT && currentCluster?.title ? currentCluster.title : '')
  }, [clusterFormMode, currentCluster?.uid])

  // Escape key listener
  useEffect(() => {
    const handleKeyDown = event => {
      const keyCode = event.keyCode || event.which
      if (keyCode === 27 && clusterFormMode !== CLUSTER_FORM_MODE.OFF) {
        handleCancel(true)
        return false
      }
      return true
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [clusterFormMode])

  // Cleanup on unmount
  useEffect(
    () => () => {
      abortControllerRef.current?.abort()
    },
    []
  )

  const callActionWithCRUDKey = async action => {
    setIsLoading(true)
    hideError()

    try {
      const { acquired, key } = await withKeyReissue({ onFetchCRUDKey }, action)

      if (!acquired) {
        setIsLoading(false)
        showError(key?.error || DEFAULT_ERROR)
      }
    } catch (error) {
      setIsLoading(false)
      showError(error?.message || DEFAULT_ERROR)
    }
  }

  const handleSave = async () => {
    if (isLoading) return

    const lassoData = getLassoData?.()
    const clusterModel = calcClusterModel({ ...currentCluster, title }, lassoData)

    if (!currentCluster?.uid) {
      // Create new cluster
      await callActionWithCRUDKey(async key => {
        try {
          const data = {
            color: clusterModel.color,
            border: clusterModel.border,
            title: clusterModel.title,
            clusterType: clusterModel.clusterType
          }

          const res = await createCluster({
            keyCode: key.code,
            clusterData: data,
            visualPosition
          })

          const newCluster = { ...clusterModel, uid: res.asset.uid, title: res.asset.title }
          const updatedClusters = [...(clusters || []), newCluster]

          updateSurveyAsset(HM_CLUSTER, res.asset)
          onChangeModel({ clusters: updatedClusters })
          onUpdateData?.()
          handleCancel(true)
        } catch (error) {
          // a refused key goes back to the re-issue wrapper, which asks for a fresh one and sends this again
          if (error instanceof CrudKeyRefusedError) throw error
          setIsLoading(false)
          showError(error?.message || DEFAULT_ERROR)
        }
      })
    } else {
      // Update existing cluster
      await callActionWithCRUDKey(async key => {
        try {
          const data = {
            uid: clusterModel.uid,
            color: clusterModel.color,
            border: clusterModel.border,
            title: clusterModel.title,
            clusterType: clusterModel.clusterType,
            ...(clusterModel.attributes && { attributes: clusterModel.attributes })
          }

          const res = await updateCluster({
            keyCode: key.code,
            clusterData: data,
            visualPosition
          })

          const updatedCluster = {
            ...clusterModel,
            title: res.asset.title,
            off: false
          }

          const updatedClusters = (clusters || []).map(c => (c.uid === clusterModel.uid ? updatedCluster : c))

          updateSurveyAsset(HM_CLUSTER, res.asset)
          onChangeModel({ clusters: updatedClusters })
          onUpdateData?.()
          handleCancel(true)
        } catch (error) {
          // a refused key goes back to the re-issue wrapper, which asks for a fresh one and sends this again
          if (error instanceof CrudKeyRefusedError) throw error
          setIsLoading(false)
          showError(error?.message || DEFAULT_ERROR)
        }
      })
    }
  }

  const handleDelete = async () => {
    if (isLoading || !currentCluster?.uid) return

    await callActionWithCRUDKey(async key => {
      try {
        await deleteCluster({
          keyCode: key.code,
          uid: currentCluster.uid,
          visualPosition
        })

        const updatedClusters = (clusters || []).filter(c => c.uid !== currentCluster.uid)

        deleteSurveyAsset(HM_CLUSTER, currentCluster.uid)
        onChangeModel({ clusters: updatedClusters })
        onUpdateData?.()
        handleCancel(true)
      } catch (error) {
        // a refused key goes back to the re-issue wrapper, which asks for a fresh one and sends this again
        if (error instanceof CrudKeyRefusedError) throw error
        setIsLoading(false)
        showError(error?.message || DEFAULT_ERROR)
      }
    })
  }

  return {
    title,
    setTitle,
    isLoading,
    error,
    isSaveDisabled,
    handleSave,
    handleDelete,
    handleCancel
  }
}
